import type { AnySceneObjectT, GroupStyleI } from "tweenly"
import { matrix as matrixTemplate } from "../data/styles/groups/transform.styles"
import {
    applyTransform,
    IDENTITY,
    isAxisAligned,
    LinearTransform,
    multiplyTransforms,
} from "./orientedResize.helpers"
import { getRotation, setRotation, toDegrees } from "./transform.helpers"

/**
 * Scaling a turned member of a multiselection exactly the way Figma does it: the member is
 * stretched by the same scale as the selection's box, as if the whole selection were one
 * picture — a turned square stretched sideways becomes a rhombus, a turned ellipse a wider
 * ellipse. Only then does every member keep filling the box it was drawn in, so the box
 * under the pointer is the box the objects end up in.
 *
 * Pure: no DOM, no Redux, no React.
 */

const EPSILON = 1e-9

const radians = (value: unknown, unit?: string) =>
    (toDegrees(Number(value) || 0, unit) * Math.PI) / 180

/** The four numbers of a `matrix` transform's value ("a, b, c, d, e, f"), plus its shift. */
const parseMatrix = (value: unknown): { linear: LinearTransform; e: number; f: number } => {
    const numbers = String(value ?? "")
        .split(",")
        .map((part) => part.trim())
        .map((part) => (part === "" ? NaN : Number(part)))
    // anything but six numbers is not a matrix CSS would draw: treat it as none
    if (numbers.length !== 6 || !numbers.every(Number.isFinite)) {
        return { linear: IDENTITY, e: 0, f: 0 }
    }
    const [a, b, c, d, e, f] = numbers
    return { linear: { a, b, c, d }, e, f }
}

/** The 2D linear part of one transform — what it does to the object's shape. */
const linearOf = (transform: GroupStyleI): LinearTransform => {
    const { property, value, unit } = transform
    switch (property) {
        case "scaleX":
            return { a: Number(value) || 0, b: 0, c: 0, d: 1 }
        case "scaleY":
            return { a: 1, b: 0, c: 0, d: Number(value) || 0 }
        case "rotateZ": {
            const angle = radians(value, unit)
            return {
                a: Math.cos(angle),
                b: Math.sin(angle),
                c: -Math.sin(angle),
                d: Math.cos(angle),
            }
        }
        // turned out of the screen: seen flat, the axis shortens by the cosine
        case "rotateX":
            return { a: 1, b: 0, c: 0, d: Math.cos(radians(value, unit)) }
        case "rotateY":
            return { a: Math.cos(radians(value, unit)), b: 0, c: 0, d: 1 }
        case "skewX":
            return { a: 1, b: 0, c: Math.tan(radians(value, unit)), d: 1 }
        case "skewY":
            return { a: 1, b: Math.tan(radians(value, unit)), c: 0, d: 1 }
        case "matrix":
            return parseMatrix(value).linear
        default:
            // translations move the object but do not change its shape
            return IDENTITY
    }
}

/** All of the object's own transforms together, in the order CSS applies them. */
export const ownLinearTransform = (transforms: GroupStyleI[] = []): LinearTransform =>
    transforms.reduce(
        (total, transform) => multiplyTransforms(total, linearOf(transform)),
        IDENTITY
    )

const isIdentity = (m: LinearTransform) =>
    Math.abs(m.a - 1) < 1e-6 &&
    Math.abs(m.b) < 1e-6 &&
    Math.abs(m.c) < 1e-6 &&
    Math.abs(m.d - 1) < 1e-6

/** Off by no more than six-decimal rounding taken through a few matrices. */
const isNearIdentity = (m: LinearTransform) =>
    Math.abs(m.a - 1) < 1e-4 &&
    Math.abs(m.b) < 1e-4 &&
    Math.abs(m.c) < 1e-4 &&
    Math.abs(m.d - 1) < 1e-4

const invert = (m: LinearTransform): LinearTransform | null => {
    const det = m.a * m.d - m.b * m.c
    if (Math.abs(det) < EPSILON) return null
    return { a: m.d / det, b: -m.b / det, c: -m.c / det, d: m.a / det }
}

const round6 = (value: number) => Math.round(value * 1e6) / 1e6

export type AffineScaleT = {
    /** how much the member's own width and height grow */
    ratioX: number
    ratioY: number
    /** its own transform afterwards — what it is drawn through */
    linear: LinearTransform
    /** its new transforms, when they change */
    transforms?: GroupStyleI[]
}

/**
 * How a TURNED member takes the selection being scaled by (scaleX, scaleY) along the canvas
 * — or null for a member that is not turned (it simply takes the two scales) or whose
 * transforms cannot carry the result.
 *
 * The member must end up drawn through S·M (S the selection's scale, M its own transform).
 * That is split into a new size and what is left over: with Q the transforms before its
 * `matrix` and D the size change, Q·X·D = S·M gives X = Q⁻¹·S·M·D⁻¹. D takes all of the
 * stretch (X's first column is a unit vector and det X = 1), so X only shears — the member's
 * rotation, its borders and its font stay as they are, the way Figma keeps strokes.
 *
 * A line stays a line instead: it only gets longer and turns with the stretch.
 */
export const affineMemberScale = (
    object: AnySceneObjectT,
    scaleX: number,
    scaleY: number
): AffineScaleT | null => affineMemberStretch(object, { a: scaleX, b: 0, c: 0, d: scaleY })

/**
 * affineMemberScale for any stretch, not only one along the axes the member is placed in.
 *
 * A member inside a turned parent is placed in that parent's axes, so the selection's scale
 * along the canvas reaches it as P⁻¹·S·P (P the parent's transform) — a stretch at the
 * parent's angle. Then even an unturned member has to be sheared to keep filling its box.
 */
export const affineMemberStretch = (
    object: AnySceneObjectT,
    stretch: LinearTransform
): AffineScaleT | null => {
    const transforms = object.transforms ?? []
    const own = ownLinearTransform(transforms)
    if (isAxisAligned(own) && isAxisAligned(stretch)) return null

    if (object.type === "line") {
        const along = applyTransform(own, { x: 1, y: 0 })
        const stretched = applyTransform(stretch, along)
        const length = Math.hypot(along.x, along.y)
        if (length < EPSILON) return null
        const ratio = Math.hypot(stretched.x, stretched.y) / length
        const turn =
            ((Math.atan2(stretched.y, stretched.x) - Math.atan2(along.y, along.x)) * 180) / Math.PI
        const next = setRotation(transforms, getRotation(transforms) + turn)
        return { ratioX: ratio, ratioY: 1, linear: ownLinearTransform(next), transforms: next }
    }

    // the leftover goes into the member's LAST matrix — whatever comes after it has to be
    // shape-neutral, or the split above does not hold
    const matrixAt = transforms.map((transform) => transform.property).lastIndexOf("matrix")
    const beforeMatrix = matrixAt === -1 ? transforms : transforms.slice(0, matrixAt)
    const afterMatrix = matrixAt === -1 ? [] : transforms.slice(matrixAt + 1)
    if (!isIdentity(ownLinearTransform(afterMatrix))) return null

    const inverse = invert(ownLinearTransform(beforeMatrix))
    if (!inverse) return null
    const stretchedOwn = multiplyTransforms(stretch, own)
    const leftover = multiplyTransforms(inverse, stretchedOwn) // X·D
    const ratioX = Math.hypot(leftover.a, leftover.b)
    const ratioY = (leftover.a * leftover.d - leftover.b * leftover.c) / ratioX
    if (!(ratioX > EPSILON) || !(ratioY > EPSILON)) return null

    const shear: LinearTransform = {
        a: leftover.a / ratioX,
        b: leftover.b / ratioX,
        c: leftover.c / ratioY,
        d: leftover.d / ratioY,
    }
    const linear = multiplyTransforms(ownLinearTransform(beforeMatrix), shear)

    // a quarter turn needs no shear: leave an untouched member's transforms untouched
    const previous = matrixAt === -1 ? undefined : transforms[matrixAt]
    if (isIdentity(shear) && isIdentity(parseMatrix(previous?.value).linear)) {
        return { ratioX, ratioY, linear }
    }
    return { ratioX, ratioY, linear, transforms: writeMatrix(transforms, matrixAt, shear) }
}

/**
 * The transforms with the linear part of the `matrix` at `matrixAt` set to `linear` — its
 * shift kept — or with a new `matrix` appended when there is none (`matrixAt` -1).
 */
const writeMatrix = (
    transforms: GroupStyleI[],
    matrixAt: number,
    linear: LinearTransform
): GroupStyleI[] => {
    const previous = matrixAt === -1 ? undefined : transforms[matrixAt]
    const { e, f } = parseMatrix(previous?.value)
    const value = [linear.a, linear.b, linear.c, linear.d, e, f].map(round6).join(", ")
    if (previous) {
        return transforms.map((transform, index) =>
            index === matrixAt ? { ...transform, value } : transform
        )
    }
    return [
        ...transforms,
        {
            ...matrixTemplate,
            value,
            index: transforms.length
                ? Math.max(...transforms.map((transform) => transform.index)) + 1
                : 0,
        },
    ]
}

/**
 * The transforms changed so that, all together, they draw the object through `target` — by
 * its last `matrix` alone (added when it has none), so its rotation, scales and the rest keep
 * their values. Unchanged when no matrix is needed for it; null when the transforms around the
 * matrix cannot be undone (a scale of 0).
 */
export const withOwnLinear = (
    transforms: GroupStyleI[],
    target: LinearTransform
): GroupStyleI[] | null => {
    const matrixAt = transforms.map((transform) => transform.property).lastIndexOf("matrix")
    const beforeMatrix = matrixAt === -1 ? transforms : transforms.slice(0, matrixAt)
    const afterMatrix = matrixAt === -1 ? [] : transforms.slice(matrixAt + 1)
    const beforeInverse = invert(ownLinearTransform(beforeMatrix))
    const afterInverse = invert(ownLinearTransform(afterMatrix))
    if (!beforeInverse || !afterInverse) return null
    // Q·X·A = target, with Q what comes before the matrix and A what comes after it
    const solved = multiplyTransforms(multiplyTransforms(beforeInverse, target), afterInverse)
    // stored to six decimals, a matrix taken there and back is a hair off: that is none
    const linear = isNearIdentity(solved) ? IDENTITY : solved
    const previous = matrixAt === -1 ? undefined : transforms[matrixAt]
    if (isIdentity(linear) && isIdentity(parseMatrix(previous?.value).linear)) return transforms
    return writeMatrix(transforms, matrixAt, linear)
}

/** What every ancestor of `parentId`, itself included, does to its content, put together. */
export const parentsLinearTransform = (
    parentId: string | null | undefined,
    objectsById: Record<string, AnySceneObjectT>
): LinearTransform => {
    let total = IDENTITY
    for (let id = parentId; id; id = objectsById[id]?.parentId) {
        const parent = objectsById[id]
        if (!parent) break
        total = multiplyTransforms(ownLinearTransform(parent.transforms), total)
    }
    return total
}

/**
 * An object's transforms for it to be drawn under `toParentId` exactly as it is under
 * `fromParentId` (null: the root) — its `matrix` making up for the difference between the two
 * sets of parents: N·M' = O·M, so M' = N⁻¹·O·M (O, N the parents' transforms put together, M
 * its own). Its rotation and every other value stay as they are.
 *
 * A parent's transform applies to everything inside it, so without this a child under a
 * turned or skewed parent is drawn turned or skewed with it. Taken back where it came from,
 * the matrix is what it was before: nothing piles up.
 */
export const transformsUnderParent = (
    transforms: GroupStyleI[],
    fromParentId: string | null | undefined,
    toParentId: string | null | undefined,
    objectsById: Record<string, AnySceneObjectT>
): GroupStyleI[] => {
    const before = parentsLinearTransform(fromParentId, objectsById)
    const after = invert(parentsLinearTransform(toParentId, objectsById))
    if (!after) return transforms
    const change = multiplyTransforms(after, before)
    if (isNearIdentity(change)) return transforms
    const target = multiplyTransforms(change, ownLinearTransform(transforms))
    return withOwnLinear(transforms, target) ?? transforms
}
